import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSaveA2pDraftMutation, useSubmitA2pCustomerProfileMutation, useGetHomeQuery } from "@/store/rest_api";
import A2pRegistration from "@/model/interface/A2pRegistration";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import A2pPricingSummary from "./A2pPricingSummary";
import { BUSINESS_TYPES, INDUSTRIES, JOB_POSITIONS } from "./a2pConstants";

const businessInfoSchema = z.object({
  businessName: z.string().min(1, "Business name is required"),
  businessType: z.string().min(1, "Business type is required"),
  ein: z.string().optional(),
  businessIndustry: z.string().min(1, "Industry is required"),
  websiteUrl: z.string().optional(),
  regionsOfOperation: z.string().optional(),
  street: z.string().min(1, "Street address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postalCode: z.string().min(1, "ZIP code is required"),
  country: z.string(),
  repFirstName: z.string().min(1, "First name is required"),
  repLastName: z.string().min(1, "Last name is required"),
  repEmail: z.string().email("Valid email is required"),
  repPhone: z.string().min(1, "Phone is required"),
  repTitle: z.string().optional(),
  repJobPosition: z.string().min(1, "Job position is required"),
  skipAutomaticSecVet: z.boolean().optional(),
  proofMobile: z.string().optional(),
});

type BusinessInfoValues = z.infer<typeof businessInfoSchema>;

interface Props {
  registration: A2pRegistration | null;
}

export default function BusinessInfoStep({ registration }: Props) {
  const [saveDraft, { isLoading: isSaving }] = useSaveA2pDraftMutation();
  const [submitProfile, { isLoading: isSubmitting }] = useSubmitA2pCustomerProfileMutation();
  const { data: home } = useGetHomeQuery();

  const form = useForm<BusinessInfoValues>({
    resolver: zodResolver(businessInfoSchema),
    defaultValues: {
      businessName: "",
      businessType: "",
      ein: "",
      businessIndustry: "",
      websiteUrl: "",
      regionsOfOperation: "US_AND_CANADA",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      repFirstName: "",
      repLastName: "",
      repEmail: "",
      repPhone: "",
      repTitle: "",
      repJobPosition: "",
      skipAutomaticSecVet: false,
      proofMobile: "",
    },
  });

  const selectedBusinessType = form.watch("businessType");
  const organisationBusinessType = home?.activeOrganisation?.businessType;
  const soleProprietor = (selectedBusinessType || organisationBusinessType) === "SOLE_PROPRIETOR";
  const einPathWarning =
    organisationBusinessType === "SOLE_PROPRIETOR" &&
    !!selectedBusinessType &&
    selectedBusinessType !== "SOLE_PROPRIETOR";
  const skipSecVet = !!form.watch("skipAutomaticSecVet");

  useEffect(() => {
    if (registration) {
      form.reset({
        businessName: registration.businessName || "",
        businessType: registration.businessType || "",
        ein: registration.ein || "",
        businessIndustry: registration.businessIndustry || "",
        websiteUrl: registration.websiteUrl || "",
        regionsOfOperation: registration.regionsOfOperation || "US_AND_CANADA",
        street: registration.street || "",
        city: registration.city || "",
        state: registration.state || "",
        postalCode: registration.postalCode || "",
        country: registration.country || "US",
        repFirstName: registration.repFirstName || "",
        repLastName: registration.repLastName || "",
        repEmail: registration.repEmail || "",
        repPhone: registration.repPhone || "",
        repTitle: registration.repTitle || "",
        repJobPosition: registration.repJobPosition || "",
        skipAutomaticSecVet: registration.skipAutomaticSecVet ?? false,
        proofMobile: registration.proofMobile || "",
      });
    }
  }, [registration]);

  const handleSaveDraft = async () => {
    const values = form.getValues();
    try {
      await saveDraft(values).unwrap();
      toast.success("Draft saved");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to save draft");
    }
  };

  const onSubmit = async (values: BusinessInfoValues) => {
    try {
      await submitProfile(values).unwrap();
      toast.success("Customer profile submitted for review");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to submit customer profile");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Business Information</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3">Business Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="businessName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Legal Business Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="businessType" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Type *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {BUSINESS_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                {einPathWarning && (
                  <Alert className="md:col-span-2">
                    <AlertDescription>
                      Your account was set up as a sole proprietor without an EIN. Registering as{" "}
                      {BUSINESS_TYPES.find((type) => type.value === selectedBusinessType)?.label ||
                        "this business type"}{" "}
                      requires an EIN from the IRS. You can continue if you have one.
                    </AlertDescription>
                  </Alert>
                )}
                {!soleProprietor && (
                  <FormField control={form.control} name="ein" render={({ field }) => (
                    <FormItem>
                      <FormLabel>EIN (Tax ID) *</FormLabel>
                      <FormControl><Input placeholder="XX-XXXXXXX" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
                {soleProprietor && (
                  <FormField control={form.control} name="proofMobile" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Owner mobile (for verification) *</FormLabel>
                      <FormControl><Input placeholder="+1..." {...field} /></FormControl>
                      <p className="text-xs text-muted-foreground">
                        A personal US or Canadian mobile, not a virtual or VoIP number. We text a code you reply to.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
                <FormField control={form.control} name="businessIndustry" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {INDUSTRIES.map((i) => (
                          <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="websiteUrl" render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Website URL</FormLabel>
                    <FormControl><Input placeholder="https://" {...field} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="skipAutomaticSecVet" render={({ field }) => (
                  <FormItem className="md:col-span-2 flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5 pr-4">
                      <FormLabel>Low-volume registration</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Skips secondary vetting: lower cost and faster approval, but a lower trust score and daily message limits. Leave off for standard high-volume registration.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )} />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Business Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="street" render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Street Address *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="city" render={({ field }) => (
                  <FormItem>
                    <FormLabel>City *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="state" render={({ field }) => (
                  <FormItem>
                    <FormLabel>State *</FormLabel>
                    <FormControl><Input placeholder="e.g. CA" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="postalCode" render={({ field }) => (
                  <FormItem>
                    <FormLabel>ZIP Code *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="country" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl><Input {...field} disabled /></FormControl>
                  </FormItem>
                )} />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Authorized Representative</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="repFirstName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repLastName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repEmail" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl><Input type="email" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repPhone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone *</FormLabel>
                    <FormControl><Input placeholder="+1..." {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repTitle" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="repJobPosition" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Position *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {JOB_POSITIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <A2pPricingSummary soleProprietor={soleProprietor} skipAutomaticSecVet={skipSecVet} balance={home?.activeOrganisation?.balance} />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
              {isSaving && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
              {isSaving ? "Saving..." : "Save Draft"}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
              {isSubmitting ? "Submitting..." : "Submit for Review"}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
